/**
 * VersionHistory - Horizontal scrollable thumbnail strip for version history
 *
 * Displays all versions of a photo in a horizontal strip, allowing users
 * to browse and select different versions.
 *
 * Feature: 081-photo-versioning (T043, T046, T048)
 */

import { cn } from '@/lib/utils';
import { VersionThumbnail } from './VersionThumbnail';
import { usePhotoVersions, type PhotoVersion } from '@/hooks/graphql/usePhotoVersionsGraphQL';
import { Text } from '@/components/atoms';

export interface VersionHistoryProps {
  /** Photo global ID to show versions for */
  photoId: string | null;
  /** Currently selected version ID (optional - defaults to current version) */
  selectedVersionId?: string | null;
  /** Callback when a version is selected */
  onVersionSelect?: (version: PhotoVersion) => void;
  /** Optional className for styling */
  className?: string;
  /** Show loading skeleton while fetching */
  showLoading?: boolean;
  /** Compact mode - smaller thumbnails */
  compact?: boolean;
}

/**
 * Loading skeleton for version history
 */
function VersionHistorySkeleton({ compact }: { compact?: boolean }) {
  const size = compact ? 'w-12 h-12' : 'w-16 h-16';

  return (
    <div className="flex gap-2 overflow-x-auto">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            size,
            'rounded bg-surface-2 animate-pulse flex-shrink-0'
          )}
        />
      ))}
    </div>
  );
}

/**
 * Version history horizontal strip component
 */
export function VersionHistory({
  photoId,
  selectedVersionId,
  onVersionSelect,
  className,
  showLoading = true,
  compact = false,
}: VersionHistoryProps) {
  const { versions, loading, error } = usePhotoVersions(photoId);

  // Find the current version for default selection
  const currentVersion = versions.find((v) => v.isCurrent);
  const effectiveSelectedId = selectedVersionId ?? currentVersion?.id;

  // Loading state
  if (loading && showLoading && versions.length === 0) {
    return (
      <div className={cn('py-2', className)}>
        <VersionHistorySkeleton compact={compact} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn('py-2', className)}>
        <Text variant="muted" className="text-sm text-danger">
          Failed to load version history
        </Text>
      </div>
    );
  }

  // Empty state - only one version (the original)
  if (versions.length <= 1) {
    return null; // Don't show anything if there's only one version
  }

  return (
    <div className={cn('py-2', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <Text variant="muted" className="text-xs uppercase tracking-wide">
          Version History ({versions.length})
        </Text>
      </div>

      {/* Scrollable thumbnail strip */}
      <div
        className={cn(
          'flex gap-2 overflow-x-auto pb-2',
          // Custom scrollbar styling
          'scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent'
        )}
      >
        {versions.map((version) => (
          <VersionThumbnail
            key={version.id}
            version={version}
            isSelected={version.id === effectiveSelectedId}
            onClick={() => onVersionSelect?.(version)}
            compact={compact}
          />
        ))}
      </div>
    </div>
  );
}

export default VersionHistory;
